import { useRef } from 'react';
import type { ReactNode } from 'react';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import { Box, Flex, FormLayout, FormLayoutGroup, TextField, TextLabel } from '@customerio/pluma-components/react';
import {
  IS_STRIPE_TEST_MODE,
  stripeCardDetailOptions,
  stripeCardNumberOptions,
} from '@/services/billing/stripe/elements';

type CardFieldName = 'number' | 'expiry' | 'cvc';

type CardFieldsProps = {
  /**
   * Reports whether all three Stripe card inputs (number, expiry, cvc) are complete. Consumers use
   * it to gate the submit button. Not called in test mode, where the fields render as plain inputs.
   */
  onChange: (isComplete: boolean) => void;
  /** Shows only the card number, for a form that opens the rest once the number is focused. */
  isDetailsHidden?: boolean;
  onNumberFocus?: () => void;
  /** Shown at the end of the card number's label row, such as accepted card brands. */
  numberLabelAccessory?: ReactNode;
};

/**
 * The Stripe card-entry inputs (card number, then expiry + cvc two-up). Must be rendered inside a
 * Stripe `<Elements>` provider. Stripe derives the full card from the card-number element, so the
 * consuming form reads that element from `useElements()` at submit time; this component only surfaces
 * completeness. Uses Pluma `FormLayout`/`FormLayoutGroup` so expiry/cvc fill their columns (Ember parity).
 */
export function CardFields({ onChange, isDetailsHidden, onNumberFocus, numberLabelAccessory }: CardFieldsProps) {
  // A ref (not state) so completeness is recomputed and reported synchronously on each field change,
  // with no re-render of this component and no effect.
  const completion = useRef<Record<CardFieldName, boolean>>({ number: false, expiry: false, cvc: false });

  const updateField = (field: CardFieldName, isComplete: boolean) => {
    completion.current = { ...completion.current, [field]: isComplete };
    const { number, expiry, cvc } = completion.current;
    onChange(number && expiry && cvc);
  };

  if (IS_STRIPE_TEST_MODE) {
    return (
      <MockCardFields
        isDetailsHidden={isDetailsHidden}
        onNumberFocus={onNumberFocus}
        numberLabelAccessory={numberLabelAccessory}
      />
    );
  }

  return (
    <FormLayout gap="150">
      <CardField label="Card number" testId="card-number" labelAccessory={numberLabelAccessory}>
        <CardNumberElement
          options={stripeCardNumberOptions()}
          onChange={(event) => updateField('number', event.complete)}
          onFocus={onNumberFocus}
        />
      </CardField>
      {isDetailsHidden === true ? null : (
        <FormLayoutGroup gap="150">
          <CardField label="Expiration" testId="card-expiry">
            <CardExpiryElement
              options={stripeCardDetailOptions()}
              onChange={(event) => updateField('expiry', event.complete)}
            />
          </CardField>
          <CardField label="CVC" testId="card-cvc">
            <CardCvcElement
              options={stripeCardDetailOptions()}
              onChange={(event) => updateField('cvc', event.complete)}
            />
          </CardField>
        </FormLayoutGroup>
      )}
    </FormLayout>
  );
}

type CardFieldProps = {
  children: ReactNode;
  label: string;
  testId: 'card-cvc' | 'card-expiry' | 'card-number';
  labelAccessory?: ReactNode;
};

function CardField({ children, label, testId, labelAccessory }: CardFieldProps) {
  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" gap="100" mb="075">
        <TextLabel>{label}</TextLabel>
        {labelAccessory}
      </Flex>
      <div
        data-test-card-cvc-entry={testId === 'card-cvc' ? true : undefined}
        data-test-card-expiry-entry={testId === 'card-expiry' ? true : undefined}
        data-test-card-number-entry={testId === 'card-number' ? true : undefined}
      >
        {children}
      </div>
    </Box>
  );
}

/**
 * Test-mode stand-in for the Stripe iframes: plain inputs so tests can render and interact with the
 * form without loading Stripe.js. Card completeness is bypassed in test mode by the consuming form.
 */
type MockCardFieldsProps = Pick<CardFieldsProps, 'isDetailsHidden' | 'onNumberFocus' | 'numberLabelAccessory'>;

function MockCardFields({ isDetailsHidden, onNumberFocus, numberLabelAccessory }: MockCardFieldsProps) {
  return (
    <FormLayout gap="150">
      <CardField label="Card number" testId="card-number" labelAccessory={numberLabelAccessory}>
        <TextField ariaLabel="Card number" placeholder="1234 1234 1234 1234" onFocus={onNumberFocus} />
      </CardField>
      {isDetailsHidden === true ? null : (
        <FormLayoutGroup gap="150">
          <TextField label="Expiration" placeholder="MM / YY" data-test-card-expiry-entry={true} />
          <TextField label="CVC" placeholder="CVC" data-test-card-cvc-entry={true} />
        </FormLayoutGroup>
      )}
    </FormLayout>
  );
}
